import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Gesture } from '@material-ui/icons';
import {
  CertificateDesign,
  CertificateField,
  FIELD_LABELS,
  IMAGE_FIELDS,
  fontCss,
  pageSize,
} from './certificate-model';

const PRIMARY = '#796FFF';
const MM_PER_PT = 25.4 / 72;

const useStyles = makeStyles(() =>
  createStyles({
    page: {
      position: 'relative',
      width: '100%',
      background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,.12), 0 1px 2px rgba(0,0,0,.24)',
      overflow: 'hidden',
      userSelect: 'none',
    },
    background: { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' },
    empty: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#9E9E9E',
      fontSize: '14px',
      textAlign: 'center',
      padding: '24px',
    },
    field: {
      position: 'absolute',
      cursor: 'move',
      outline: '1px dashed transparent',
      '&:hover': { outlineColor: '#BDBDBD' },
    },
    selected: { outline: `1px dashed ${PRIMARY} !important` },
    handle: {
      position: 'absolute',
      right: '-5px',
      top: '50%',
      width: '10px',
      height: '10px',
      marginTop: '-5px',
      borderRadius: '50%',
      background: PRIMARY,
      cursor: 'ew-resize',
    },
    placeholder: {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      background: '#EEEEEE',
      color: '#9E9E9E',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  })
);

interface Props {
  design: CertificateDesign;
  selectedId?: string;
  onSelect: (id?: string) => void;
  onChangeField: (id: string, patch: Partial<CertificateField>) => void;
}

interface DragState {
  id: string;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  field: CertificateField;
}

export const CustomDesignCanvas = ({ design, selectedId, onSelect, onChangeField }: Props) => {
  const classes = useStyles();
  const pageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState>();
  const [pagePx, setPagePx] = useState(0);
  const page = pageSize(design.orientation);
  const pxPerMm = pagePx / page.width;

  useEffect(() => {
    const measure = () => setPagePx(pageRef.current?.clientWidth ?? 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [design.orientation]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || !pxPerMm) return;
      const dx = (e.clientX - drag.startX) / pxPerMm;
      const dy = (e.clientY - drag.startY) / pxPerMm;
      if (drag.mode === 'move') {
        onChangeField(drag.id, {
          x: Math.round(Math.max(0, Math.min(page.width - 5, drag.field.x + dx)) * 10) / 10,
          y: Math.round(Math.max(0, Math.min(page.height - 5, drag.field.y + dy)) * 10) / 10,
        });
      } else {
        onChangeField(drag.id, {
          width: Math.round(Math.max(5, Math.min(page.width - drag.field.x, drag.field.width + dx)) * 10) / 10,
        });
      }
    };
    const onUp = () => {
      dragRef.current = undefined;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [pxPerMm, page.width, page.height, onChangeField]);

  const startDrag = (e: React.PointerEvent, field: CertificateField, mode: DragState['mode']) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect(field.id);
    dragRef.current = { id: field.id, mode, startX: e.clientX, startY: e.clientY, field };
  };

  const fieldText = (field: CertificateField) => (field.key === 'TEXT' ? field.text : `{{${field.key}}}`);

  const renderImage = (field: CertificateField) => {
    const signature = field.key === 'SIGNATURE_1' ? design.signatories[0] : design.signatories[1];
    if (signature?.image) {
      return <img src={signature.image.url} alt='' style={{ maxWidth: '100%', maxHeight: '100%' }} />;
    }
    return (
      <Box className={classes.placeholder} title={FIELD_LABELS[field.key]}>
        <Gesture />
      </Box>
    );
  };

  return (
    <Box
      className={classes.page}
      style={{ paddingTop: `${(page.height / page.width) * 100}%` }}
      onPointerDown={() => onSelect(undefined)}
    >
      <div ref={pageRef} style={{ position: 'absolute', inset: 0 }}>
        {design.background ? (
          <img className={classes.background} src={design.background.url} alt='' />
        ) : (
          <Typography className={classes.empty}>Upload your design to use as the background</Typography>
        )}
        {pxPerMm > 0 &&
          design.fields.map((field) => {
            const image = IMAGE_FIELDS.includes(field.key);
            return (
              <Box
                key={field.id}
                className={`${classes.field} ${selectedId === field.id ? classes.selected : ''}`}
                onPointerDown={(e: React.PointerEvent) => startDrag(e, field, 'move')}
                style={{
                  left: field.x * pxPerMm,
                  top: field.y * pxPerMm,
                  width: field.width * pxPerMm,
                  height: image ? field.width * pxPerMm : undefined,
                  textAlign: field.align,
                  display: image ? 'flex' : 'block',
                  justifyContent:
                    field.align === 'left' ? 'flex-start' : field.align === 'right' ? 'flex-end' : 'center',
                  fontFamily: fontCss(field.font),
                  fontSize: field.size * MM_PER_PT * pxPerMm,
                  fontWeight: field.bold ? 700 : 400,
                  color: field.colour,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                }}
              >
                {image ? renderImage(field) : fieldText(field) || FIELD_LABELS[field.key]}
                {selectedId === field.id && (
                  <span
                    className={classes.handle}
                    onPointerDown={(e: React.PointerEvent) => startDrag(e, field, 'resize')}
                  />
                )}
              </Box>
            );
          })}
      </div>
    </Box>
  );
};
